/**
 * App-side wrappers over the QC engine (spec §5). Thin by design: they bind a
 * preset name to the shipped ruleset and always re-evaluate after a fix so the
 * UI shows a fresh, consistent issue list (auto-fixers must introduce no new
 * issues — asserted by the core suites).
 */
import {
  autoFix,
  evaluate,
  PRESETS,
  type Cue,
  type PresetName,
  type QCIssue,
  type RuleId,
  type Ruleset,
  type SubtitleDocument,
} from '../../core';
import { plural } from '../text';

/**
 * A ruleset the engine can run without touching storage: either a built-in
 * preset name (the worker resolves it via its own static `PRESETS`) or an
 * already-resolved `Ruleset` object. Deliberately NOT a `PresetId` — a
 * compile-time guardrail forcing callers to resolve custom ids first, since
 * `engine.ts` is statically imported by the batch worker, which cannot read
 * `localStorage` (spec §4).
 */
export type Resolvable = PresetName | Ruleset;

const toRuleset = (preset: Resolvable): Ruleset =>
  typeof preset === 'string' ? PRESETS[preset] : preset;

/** Evaluate a document against a preset name or a pre-resolved ruleset. */
export function runQc(doc: SubtitleDocument, preset: Resolvable): QCIssue[] {
  return evaluate(doc, toRuleset(preset));
}

/**
 * Whether an issue's auto-fix would actually help (cue-forge-8b4): the engine's
 * per-cue `effectivelyFixable` verdict when present (conditionally-fixable rules
 * like `max-chars-per-line`, whose re-wrap is reverted when it would breach
 * `max-lines-per-cue`), otherwise the rule's static `fixable` flag. The single
 * place the UI decides Fix-vs-remedy, so a dead Fix button never shows.
 */
export function isFixable(issue: QCIssue): boolean {
  return issue.effectivelyFixable ?? issue.fixable;
}

/** How many of the given issues have an auto-fix that would actually help. */
export function fixableCount(issues: QCIssue[]): number {
  return issues.reduce((n, i) => (isFixable(i) ? n + 1 : n), 0);
}

/** The reconciled QC counts plus a single human legend (cue-forge-n3e.3). */
export interface QcLegend {
  total: number;
  errors: number;
  warnings: number;
  fixable: number;
  /** Issues the operator marked intentional (cue-forge-n3e.7), excluded above. */
  accepted: number;
  /** e.g. "12 issues · 3 errors · 9 warnings · 5 auto-fixable · 2 accepted". */
  text: string;
}

/**
 * Reconcile the scattered QC counts into one legend so a single line carries the
 * whole picture (total / errors / warnings / auto-fixable) instead of the counts
 * living apart from the list. `issues` is the ACTIVE list (accepted issues are
 * already excluded, cue-forge-n3e.7); pass `acceptedCount` to append a trailing
 * "· N accepted" segment so the operator can see how many were set aside.
 */
export function qcLegend(issues: QCIssue[], acceptedCount = 0): QcLegend {
  const total = issues.length;
  const errors = issues.reduce((n, i) => (i.severity === 'error' ? n + 1 : n), 0);
  const warnings = total - errors;
  const fixable = fixableCount(issues);
  let text = `${plural(total, 'issue')} · ${plural(errors, 'error')} · ${plural(
    warnings,
    'warning',
  )} · ${fixable} auto-fixable`;
  if (acceptedCount > 0) text += ` · ${acceptedCount} accepted`;
  return { total, errors, warnings, fixable, accepted: acceptedCount, text };
}

export interface FixOutcome {
  doc: SubtitleDocument;
  /** Re-evaluated issues on the fixed document. */
  issues: QCIssue[];
  /** Number of issues the fixer resolved. */
  fixedCount: number;
}

/**
 * Auto-fix `toFix` against `preset`, then re-run QC on the result. `ruleIds`
 * scopes which fixers run (used by fix-one to fix a single rule's issue).
 * `protect` shields the operator's accepted cues from the whole-document fixers
 * (cue-forge-n3e.7), so a fix elsewhere never rewrites an intentional cue.
 */
export function applyFix(
  doc: SubtitleDocument,
  preset: Resolvable,
  toFix: QCIssue[],
  ruleIds?: RuleId[],
  protect?: ReadonlySet<Cue>,
): FixOutcome {
  const ruleset = toRuleset(preset);
  const result = autoFix(doc, toFix, { ruleset, enabled: ruleIds, protect });
  return {
    doc: result.doc,
    issues: evaluate(result.doc, ruleset),
    fixedCount: result.fixed.length,
  };
}
